import { useState } from 'react'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { LuFactory } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { plantasService, companiesService } from '../../api/services'
import { usePagedRequest } from '../../hooks'
import type { CompanyResponse } from '../../api/models/response'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { useHasPermission } from '../../auth/PermissionContext'

export default function PlantasPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const canCreate = useHasPermission('plant.analysis.create')
  const canUpdate = useHasPermission('plant.analysis.update')

  const excelColumns: ExcelColumn<CompanyResponse>[] = [
    { header: t('plantas.list.headers.empresa'), accessor: (e) => e.legalName },
    { header: t('plantas.list.headers.ruc'), accessor: (e) => e.ruc },
    { header: t('plantas.nuevo.fields.phone'), accessor: (e) => e.mainPhone ?? '' },
    { header: t('plantas.nuevo.fields.email'), accessor: (e) => e.mainEmail ?? '' },
    { header: t('plantas.list.headers.estado'), accessor: (e) => (e.status === 0 ? t('plantas.list.status.pending') : t('plantas.list.status.active')) },
  ]

  const [search, setSearch] = useState('')

  const { items: empresas, totalCount, isFetching: loading, error, refresh, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    plantasService.getAll,
    { params: { search: search.trim() || undefined } },
  )

  const getInitials = (company: CompanyResponse) => {
    const words = company.legalName.split(/\s+/)
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase()
    }
    return (words[0]?.[0] ?? '').toUpperCase()
  }

  const handleDelete = async (empresa: CompanyResponse) => {
    await companiesService.delete(empresa.id)
    refresh()
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title={t('plantas.list.title')}
        subtitle={t('plantas.list.subtitle')}
        buttonLabel={canCreate ? t('plantas.list.newButton') : undefined}
        onButtonClick={canCreate ? () => navigate('/app/plantas/nuevo') : undefined}
        buttonIcon={canCreate ? <LuFactory size={18} /> : undefined}
        colors={colors}
      />

      <DataList<CompanyResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('plantas.list.searchPlaceholder')}
        onExport={() => exportToExcel(empresas, excelColumns, 'plantas')}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="plantas"
        brandColor={brand.green}
        headers={[
          t('plantas.list.headers.empresa'),
          t('plantas.list.headers.ruc'),
          t('plantas.list.headers.contacto'),
          t('plantas.list.headers.estado'),
        ]}
        data={empresas}
        loading={loading}
        error={error}
        emptyMessage={t('plantas.list.emptyMessage')}
        loadingMessage={t('plantas.list.loadingMessage')}
        colors={colors}
        keyExtractor={(e) => e.id}
        renderRow={(empresa) => (
          <>
            {/* Avatar + Name */}
            <DataTable.Cell>
              <Flex alignItems="center" gap={3}>
                <Box
                  width="36px"
                  height="36px"
                  borderRadius="full"
                  bg={`linear-gradient(135deg, ${brand.green} 0%, ${brand.gold} 100%)`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontSize="xs" fontWeight="bold" color="white">
                    {getInitials(empresa)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={colors.text} wordBreak="break-word">
                    {empresa.legalName}
                  </Text>
                  {empresa.commercialName && (
                    <Text fontSize="xs" color={colors.textMuted}>
                      {empresa.commercialName}
                    </Text>
                  )}
                </Box>
              </Flex>
            </DataTable.Cell>

            {/* RUC */}
            <DataTable.Cell>
              <Text fontFamily="monospace" fontSize="sm" color={colors.text}>
                {empresa.ruc}
              </Text>
            </DataTable.Cell>

            {/* Contacto */}
            <DataTable.Cell>
              <Box>
                {empresa.mainPhone && (
                  <Text fontSize="sm" color={colors.text}>
                    {empresa.mainPhone}
                  </Text>
                )}
                {empresa.mainEmail && (
                  <Text fontSize="xs" color={colors.textMuted}>
                    {empresa.mainEmail}
                  </Text>
                )}
                {!empresa.mainPhone && !empresa.mainEmail && (
                  <Text tid="plantas.list.noContact" fontSize="xs" color={colors.textMuted} />
                )}
              </Box>
            </DataTable.Cell>

            {/* Estado */}
            <DataTable.Cell>
              <Box
                display="inline-block"
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="10px"
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="0.05em"
                bg={empresa.status === 0 ? 'rgba(212, 154, 42, 0.2)' : 'rgba(42, 138, 135, 0.2)'}
                color={empresa.status === 0 ? '#d49a2a' : '#2a8a87'}
              >
                {empresa.status === 0 ? t('plantas.list.status.pending') : t('plantas.list.status.active')}
              </Box>
            </DataTable.Cell>
          </>
        )}
        actions={(empresa) => {
          const acts: { label: string; onClick: () => void; color?: string }[] = []
          if (canUpdate) {
            acts.push(
              empresa.status === 0
                ? { label: t('plantas.list.actions.continueReg'), onClick: () => navigate(`/app/plantas/${empresa.id}`) }
                : { label: t('plantas.list.actions.edit'), onClick: () => navigate(`/app/plantas/${empresa.id}`) },
            )
          }
          if (canUpdate) acts.push({ label: t('plantas.list.actions.delete'), onClick: () => handleDelete(empresa), color: '#c8442f' })
          return acts
        }}
      />
    </VStack>
  )
}
